import { useState } from 'react';
import {
  IonButton,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonToolbar,
} from '@ionic/react';
import { keyOutline, send } from 'ionicons/icons';
import { useI18n } from '../i18n/I18nProvider';

export type ComposerMode = 'plain' | 'ghost' | 'coded';

export interface ChatComposerBarProps {
  value: string;
  onChange: (value: string) => void;
  mode: ComposerMode;
  onModeChange: (mode: ComposerMode) => void;
  onSubmit: () => void;
  sending?: boolean;
  disabled?: boolean;
  /** When true, wraps in IonFooter (must be direct child of IonPage). */
  asFooter?: boolean;
  submitIcon?: typeof send;
  submitAriaLabel?: string;
}

const ChatComposerBar: React.FC<ChatComposerBarProps> = ({
  value,
  onChange,
  mode,
  onModeChange,
  onSubmit,
  sending = false,
  disabled = false,
  asFooter = false,
  submitIcon = send,
  submitAriaLabel,
}) => {
  const { t } = useI18n();
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  const handleModeChange = (next: string | number | undefined) => {
    if (next !== 'plain' && next !== 'ghost' && next !== 'coded') return;
    onModeChange(next);
  };

  const placeholder = () => {
    if (mode === 'coded') return t('chat.input.placeholder.encoded');
    if (mode === 'ghost') return t('chat.input.placeholder.ghost');
    return t('chat.input.placeholder.plain');
  };

  const modeLabel = () => {
    if (mode === 'coded') return t('chat.composer.mode.coded');
    if (mode === 'ghost') return t('chat.composer.mode.ghost');
    return t('chat.composer.mode.plain');
  };

  const composerRow = (
    <>
      <div className="chat-input-item">
        <IonButton
          id="composer-options-trigger"
          fill="solid"
          className={`chat-input-options chat-input-options--${mode}`}
          onClick={() => setIsOptionsOpen(true)}
          aria-label={t('chat.composer.optionsAria')}
        >
          <span className="chat-input-send-inner">
            <IonIcon icon={keyOutline} />
          </span>
        </IonButton>
        <input
          className="chat-input-field"
          type="text"
          value={value}
          placeholder={placeholder()}
          inputMode="text"
          enterKeyHint="send"
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (value.trim()) onSubmit();
            }
          }}
        />
        <IonButton
          className="chat-input-send"
          onClick={onSubmit}
          disabled={sending || disabled || !value.trim()}
          aria-label={submitAriaLabel ?? modeLabel()}
        >
          <span className="chat-input-send-inner">
            <IonIcon icon={submitIcon} />
          </span>
        </IonButton>
      </div>

      <IonPopover
        className="vault-glass-popover composer-options-popover"
        keepContentsMounted
        isOpen={isOptionsOpen}
        dismissOnSelect={false}
        onDidDismiss={() => setIsOptionsOpen(false)}
      >
        <IonList className="encode-popover">
          <IonItem lines="none">
            <div className="encode-popover-center">
              <IonText>
                <strong>{t('chat.composer.optionsTitle')}</strong>
              </IonText>
            </div>
          </IonItem>
          <IonItem lines="none" className="composer-mode-segment-item">
            <div className="composer-mode-segment-wrap">
              <IonSegment
                className={`composer-mode-segment composer-mode-segment--${mode}`}
                value={mode}
                onIonChange={(e) => handleModeChange(e.detail.value)}
              >
                <IonSegmentButton value="plain">
                  <IonLabel>{t('chat.composer.mode.plain')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="coded">
                  <IonLabel>{t('chat.composer.mode.coded')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="ghost">
                  <IonLabel>{t('chat.composer.mode.ghost')}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </div>
          </IonItem>
        </IonList>
      </IonPopover>
    </>
  );

  if (asFooter) {
    return (
      <IonFooter className="chat-footer">
        <IonToolbar>{composerRow}</IonToolbar>
      </IonFooter>
    );
  }

  return <div className="chat-composer-inline">{composerRow}</div>;
};

export default ChatComposerBar;
